import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Group, Skeleton } from "@mantine/core";
import dayjs from "dayjs";
import { ArchivedDocumentWithDetails, DeviationConfirmationStatus, InspectionDeviationResultDto } from "@/types";
import Text from "@/components/atoms/Text";
import Loader from "@/components/atoms/Loader";
import Badge, { badgeColor } from "@/components/atoms/Badge";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import { useAuth } from "@/contexts/AuthContext";
import { useGetUserById } from "@/services/Login";
import { useGetDocumentsFromString } from "@/services/Documents";

// Small pieces shared by the tab's accordions.

export const formatDateTime = (value: string) => dayjs(value).format("DD.MM.YYYY - HH:mm");

export const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <Grid.Col span={3}>
      <Text size="sm" color="secondary">
        {label}
      </Text>
    </Grid.Col>
    {/* Plain values get the value style; components (the carousel, a skeleton) bring their own. */}
    <Grid.Col span={9}>{typeof children === "string" ? <Text size="sm">{children}</Text> : children}</Grid.Col>
  </>
);

export const UserName = ({ userId }: { userId: number }) => {
  const { data: user, isLoading } = useGetUserById(userId, { enabled: !!userId });
  if (isLoading) return <Skeleton height={16} width={120} />;
  return <Text size="sm">{user ? `${user.firstname} ${user.lastname}` : "-"}</Text>;
};

export const InspectionPhotos = ({ photoIds }: { photoIds: string[] }) => {
  const { currentCompany } = useAuth();
  // Archived under both the buyer and the seller, so either side reads them as its own.
  const { images, pending } = useGetDocumentsFromString(photoIds, currentCompany?.companyId, !!currentCompany);

  if (photoIds.length === 0) return <Text size="sm">-</Text>;
  if (pending) return <Loader />;
  return (
    <ImageCarousel
      images={images.filter((image): image is ArchivedDocumentWithDetails => !!image)}
      imagesPrSlide={4}
      height={90}
    />
  );
};

/** A deviation's name, with its confirmation status when the buyer required confirmation. */
export const DeviationHeader = ({ deviation }: { deviation: InspectionDeviationResultDto }) => (
  <Grid.Col span={12}>
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm" strong>
        {deviation.name}
      </Text>
      {deviation.confirmationStatus && <ConfirmationBadge status={deviation.confirmationStatus} />}
    </Group>
  </Grid.Col>
);

/** The manager's decision: who and when for the buyer only, the comment for both sides. */
export const DeviationDecision = ({
  deviation,
  isBuyer,
}: {
  deviation: InspectionDeviationResultDto;
  isBuyer: boolean;
}) => {
  const { t } = useTranslation();
  const { decidedByUserId, decidedAt, decisionComment } = deviation;

  return (
    <>
      {isBuyer && decidedByUserId != null && (
        <Row label={t("inspections:drawer.decidedBy")}>
          <Group gap={4} wrap="nowrap" data-testid="inspection-deviation-decided-by">
            <UserName userId={decidedByUserId} />
            {/* The leading space keeps the name and the time apart in the text; the gap spaces them visually. */}
            {decidedAt && <Text size="sm">{` · ${formatDateTime(decidedAt)}`}</Text>}
          </Group>
        </Row>
      )}
      {decisionComment && <Row label={t("inspections:drawer.comment")}>{decisionComment}</Row>}
    </>
  );
};

const ConfirmationBadge = ({ status }: { status: DeviationConfirmationStatus }) => {
  const { t } = useTranslation();
  // Listed out (not built from the status) so the i18n extractor sees every key.
  const labels: Record<DeviationConfirmationStatus, string> = {
    PENDING: t("inspections:drawer.confirmationStatus.PENDING"),
    CONFIRMED: t("inspections:drawer.confirmationStatus.CONFIRMED"),
    REJECTED: t("inspections:drawer.confirmationStatus.REJECTED"),
  };
  const colors: Record<DeviationConfirmationStatus, string> = {
    PENDING: badgeColor.warningOutline,
    CONFIRMED: badgeColor.positiveOutline,
    REJECTED: badgeColor.negativeOutline,
  };

  return (
    <Badge radius="xs" color={colors[status]} data-testid="inspection-deviation-status">
      {labels[status]}
    </Badge>
  );
};
